
import { Link } from "react-router-dom"
import {
  Building2,
  Stethoscope,
  ActivitySquare,
  Heart,
  HeartPulse,
  Bed,
  Calendar,
  Shield,
  Baby,
  Users,
  Microscope,
  Pill,
  Thermometer,
  CircleDot,
  TestTube2,
  BedDouble,
  Sparkles,
  CheckCircle,
  ArrowRight,
} from "lucide-react"
import SEO from "../SEO"

import { Button } from "../../components/ui/button"
import { Card, CardContent } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"

export default function FacilitiesPage() {
  const facilitiesSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/",
    "logo": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad offering advanced medical infrastructure and patient-centric facilities.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "telephone": "+91-99660-60000",
      "availableLanguage": ["English", "Telugu", "Hindi"]
    },
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals",
      "https://www.linkedin.com/company/pranaamhospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaamhospitals.com/about/facilities"
    },
    "headline": "Advanced Hospital Facilities at Pranaam Hospitals",
    "description": "Discover the modern medical facilities at Pranaam Hospitals including ICUs, operation theatres, diagnostics, emergency services, and patient-focused infrastructure.",
    "image": "https://pranaamhospitals.com/wp-content/uploads/2025/hospital-facilities.jpg",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "Pranaam Hospitals facilities",
      "advanced hospital infrastructure Hyderabad",
      "ICU and emergency facilities Hyderabad",
      "NABH hospital infrastructure"
    ]
  }
];


  const facilities = [
    {
      name: "Out-Patient Department",
      icon: Users,
      category: "patient-care",
      description: "Comprehensive outpatient services with modern consultation rooms",
    },
    {
      name: "Emergency Department",
      icon: ActivitySquare,
      category: "emergency",
      description: "24/7 emergency services with rapid response protocols",
    },
    {
      name: "Radiology Department (CT and X-Ray)",
      icon: Microscope,
      category: "diagnostics",
      description: "Advanced imaging services with state-of-the-art equipment",
    },
    {
      name: "Pharmacy 24/7",
      icon: Pill,
      category: "support",
      description: "Round-the-clock pharmacy services for all your medication needs",
    },
    {
      name: "USG",
      icon: Thermometer,
      category: "diagnostics",
      description: "Ultrasound imaging for accurate diagnostic assessment",
    },
    {
      name: "Lab Sample Collection",
      icon: TestTube2,
      category: "diagnostics",
      description: "Convenient sample collection with quick processing times",
    },
    {
      name: "Pediatric ICU",
      icon: Baby,
      category: "critical-care",
      description: "Specialized intensive care for critically ill children",
    },
    {
      name: "Surgical ICU",
      icon: HeartPulse,
      category: "critical-care",
      description: "Post-operative intensive care with advanced monitoring",
    },
    {
      name: "Neonatal ICU",
      icon: Heart,
      category: "critical-care",
      description: "Specialized care for newborns requiring intensive support",
    },
    {
      name: "Maternity ICU",
      icon: Shield,
      category: "critical-care",
      description: "Critical care services for high-risk maternity cases",
    },
    {
      name: "Central Sterile Supply Department",
      icon: Sparkles,
      category: "support",
      description: "Ensuring the highest standards of sterilization",
    },
    {
      name: "4 Operating Theaters with Laminar Airflow",
      icon: HeartPulse,
      category: "surgical",
      description: "Modern operation theaters with advanced air filtration systems",
    },
    {
      name: "Labour Room",
      icon: BedDouble,
      category: "maternity",
      description: "Comfortable and well-equipped labour delivery rooms",
    },
    {
      name: "Department of Laboratory Medicine",
      icon: Microscope,
      category: "diagnostics",
      description: "Comprehensive laboratory services with accurate diagnostics",
    },
    {
      name: "Medical Intensive Care Unit",
      icon: Stethoscope,
      category: "critical-care",
      description: "Advanced critical care for serious medical conditions",
    },
    {
      name: "High Dependency Unit",
      icon: HeartPulse,
      category: "critical-care",
      description: "Intermediate care between ICU and general wards",
    },
    {
      name: "Isolation Rooms",
      icon: Shield,
      category: "patient-care",
      description: "Specialized rooms for infection control and patient safety",
    },
    {
      name: "Single Rooms",
      icon: Bed,
      category: "accommodation",
      description: "Private single occupancy rooms for patient comfort",
    },
    {
      name: "Quality Assurance Department",
      icon: CheckCircle,
      category: "support",
      description: "Ensuring the highest standards of healthcare quality",
    },
    {
      name: "Deluxe Rooms",
      icon: BedDouble,
      category: "accommodation",
      description: "Comfortable deluxe rooms with modern amenities",
    },
    {
      name: "Super Deluxe Rooms",
      icon: Sparkles,
      category: "accommodation",
      description: "Premium rooms with luxury amenities and services",
    },
  ]

  const stats = [
    { label: "Patient Care Facilities", value: "20+", icon: HeartPulse },
    { label: "Operating Theaters", value: "4", icon: CircleDot },
    { label: "ICU Units", value: "6", icon: HeartPulse },
    { label: "24/7 Services", value: "Multiple", icon: ActivitySquare },
  ]

  const categories = [
    { id: "all", label: "All Facilities", color: "primary" },
    { id: "critical-care", label: "Critical Care", color: "primary" },
    { id: "diagnostics", label: "Diagnostics", color: "primary" },
    { id: "patient-care", label: "Patient Care", color: "primary" },
    { id: "accommodation", label: "Accommodation", color: "primary" },
    { id: "support", label: "Support Services", color: "primary" },
  ]

  return (
    <>
    <SEO
  title="Hospital Facilities | Advanced Medical Infrastructure at Pranaam Hospitals"
  description="Explore the state-of-the-art facilities at Pranaam Hospitals, Hyderabad, featuring advanced medical infrastructure, modern operation theatres, ICU, diagnostics, and patient-friendly amenities."
  canonical="https://pranaamhospitals.com/about/facilities"
  robots="index, follow"
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  keywords="Pranaam Hospitals facilities, hospital infrastructure Hyderabad,
            advanced medical facilities Hyderabad, ICU facilities Hyderabad,
            NABH hospital facilities, patient friendly hospital Hyderabad"
  schema={facilitiesSchema}
/>



    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-gradient-to-r from-primary/10 to-primary/5">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                World-Class Infrastructure
              </Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Our <span className="text-primary">Facilities</span>
              </h1>
              <p className="mb-8 text-lg text-gray-700 max-w-3xl mx-auto">
                Our facilities are a window into what we can offer our patients to make their visit comfortable and
                undergo treatments with ease. From the spacious reception area that welcomes all our patients and
                visitors into the pristine environments of our examination rooms, patient rooms and other facilities
                that are visually appealing yet, follow stringent cleanliness protocols putting your comfort and safety
                first.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {stats.map((stat, index) => {
                  const Icon = stat.icon
                  return (
                    <Badge
                      key={index}
                      variant="outline"
                      className="px-4 py-2 border-primary/20 bg-white hover:bg-primary/5 transition-colors"
                    >
                      <Icon className="w-4 h-4 mr-2 text-primary" />
                      <span className="font-bold text-primary mr-1">{stat.value}</span>
                      <span className="text-gray-700">{stat.label}</span>
                    </Badge>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <div className="relative h-90 rounded-lg overflow-hidden shadow-lg">
                <img
                  src="/images/ModernHospitalFacility.png"
                  alt="Modern Hospital Facility"
                  fill
                  className="object-cover"
                />
                
              </div>
              <div>
                <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                  <Building2 className="w-4 h-4 mr-2" />
                  State-of-the-Art Infrastructure
                </Badge>
                <h2 className="mb-6 text-3xl font-bold md:text-4xl">
                  Advanced Healthcare <span className="text-primary">Environment</span>
                </h2>
                <p className="mb-6 text-gray-700">
                  At Pranaam Hospitals, we believe that healing begins with the environment. Our facilities are
                  meticulously designed to provide a seamless blend of comfort, technology, and clinical excellence.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Patient-Centric Design</h3>
                      <p className="text-gray-600 text-sm">
                        Every space is designed keeping patient comfort and recovery in mind
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Stringent Hygiene Protocols</h3>
                      <p className="text-gray-600 text-sm">
                        Hospital-grade cleanliness standards maintained across all facilities
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">Modern Medical Equipment</h3>
                      <p className="text-gray-600 text-sm">Latest technology for accurate diagnosis and treatment</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Filter Section */}
        {/* <section className="py-12 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center mb-8">
              <h2 className="text-2xl font-bold mb-4">Explore Our Facilities</h2>
              <p className="text-gray-600">Browse through our comprehensive range of healthcare facilities</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant="outline"
                  className="border-primary/20 hover:bg-primary/5 bg-transparent"
                >
                  {category.label}
                </Button>
              ))}
            </div>
          </div>
        </section> */}

        {/* Facilities Grid */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
             <div className="max-w-4xl mx-auto text-center mb-8">
              <h2 className="text-2xl font-bold mb-4">Explore Our Facilities</h2>
              <p className="text-gray-600">Browse through our comprehensive range of healthcare facilities</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {facilities.map((facility, index) => {
                const Icon = facility.icon
                return (
                  <Card
                    key={index}
                    className="group hover:shadow-lg transition-all duration-300 hover:translate-y-[-4px] border-gray-200"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary group-hover:text-white transition-colors">
                          <Icon className="w-6 h-6 text-primary group-hover:text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold mb-2 text-gray-900 group-hover:text-primary transition-colors">
                            {facility.name}
                          </h3>
                          <p className="text-sm text-gray-600">{facility.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
        </section>

        {/* Critical Care Excellence Section */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                  <HeartPulse className="w-4 h-4 mr-2" />
                  Critical Care Excellence
                </Badge>
                <h2 className="text-3xl font-bold mb-4">
                  Specialized <span className="text-primary">Intensive Care Units</span>
                </h2>
                <p className="text-gray-700 max-w-3xl mx-auto">
                  Our multiple ICU facilities are equipped with advanced life support systems and round-the-clock
                  monitoring to provide the highest level of critical care.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="p-3 rounded-lg bg-primary/10 w-fit mb-4">
                      <Baby className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-bold mb-2 text-lg">Pediatric ICU</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Specialized care for critically ill children with pediatric intensivists and child-friendly
                      environment
                    </p>
                    <Badge variant="outline" className="text-primary border-primary/20">
                      24/7 Monitoring
                    </Badge>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="p-3 rounded-lg bg-primary/10 w-fit mb-4">
                      <Heart className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-bold mb-2 text-lg">Neonatal ICU</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Advanced NICU with incubators, ventilators, and specialized neonatal care team
                    </p>
                    <Badge variant="outline" className="text-primary border-primary/20">
                      Specialized Equipment
                    </Badge>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="p-3 rounded-lg bg-primary/10 w-fit mb-4">
                      <Stethoscope className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-bold mb-2 text-lg">Medical ICU</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Comprehensive critical care with advanced monitoring and life support systems
                    </p>
                    <Badge variant="outline" className="text-primary border-primary/20">
                      Expert Team
                    </Badge>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Accommodation Options */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                  <Bed className="w-4 h-4 mr-2" />
                  Patient Accommodation
                </Badge>
                <h2 className="text-3xl font-bold mb-4">
                  Comfortable <span className="text-primary">Rooms & Amenities</span>
                </h2>
                <p className="text-gray-700 max-w-3xl mx-auto">
                  Choose from a range of accommodation options designed for your comfort and convenience during your
                  stay.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <Card className="relative overflow-hidden group hover:shadow-xl transition-shadow">
                  <div className="relative h-90">
                    <img
                      src="/images/SingleRooms.png"
                      alt="Single Room"
                      fill
                      className="object-cover"
                    />
                    
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-xl mb-2">Single Rooms</h3>
                    <p className="text-gray-600 mb-4">
                      Private single occupancy rooms with essential amenities for comfortable recovery
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        Private bathroom
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        TV & Wi-Fi
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        Attendant seating
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="relative overflow-hidden group hover:shadow-xl transition-shadow border-primary/20">
                  <Badge className="absolute top-4 right-4 z-10 bg-primary text-white">Popular</Badge>
                  <div className="relative h-90">
                    <img
                      src="/images/DeluxeRooms.png"
                      alt="Deluxe Room"
                      fill
                      className="object-cover"
                    />
                     
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-xl mb-2">Deluxe Rooms</h3>
                    <p className="text-gray-600 mb-4">
                      Spacious rooms with modern amenities and additional comfort features
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        Larger living space
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        Premium bedding
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        Mini refrigerator
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="relative overflow-hidden group hover:shadow-xl transition-shadow">
                  <Badge className="absolute top-4 right-4 z-10 bg-black text-white">Premium</Badge>
                  <div className="relative h-90">
                    <img
                      src="/images/SuperDeluxeRooms.png"
                      alt="Super Deluxe Room"
                      fill
                      className="object-cover"
                    />
                     
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-xl mb-2">Super Deluxe Rooms</h3>
                    <p className="text-gray-600 mb-4">
                      Luxury accommodation with premium amenities and personalized services
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        Luxury furnishings
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        Pantry services
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        Separate visitor area
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* 24/7 Services Section */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                  <ActivitySquare className="w-4 h-4 mr-2" />
                  Round-the-Clock Care
                </Badge>
                <h2 className="text-3xl font-bold mb-4">
                  24/7 <span className="text-primary">Services</span>
                </h2>
                <p className="text-gray-700">We're here for you anytime, day or night, with essential services</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <ActivitySquare className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2">Emergency Department</h3>
                      <p className="text-gray-600 text-sm">
                        Rapid response team ready 24/7 for all medical emergencies with advanced trauma care
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Pill className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2">Pharmacy</h3>
                      <p className="text-gray-600 text-sm">
                        24/7 pharmacy services ensuring medication availability at all times
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <TestTube2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2">Laboratory Services</h3>
                      <p className="text-gray-600 text-sm">
                        Round-the-clock lab testing with quick turnaround times for urgent cases
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Microscope className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2">Radiology</h3>
                      <p className="text-gray-600 text-sm">
                        24/7 imaging services including X-rays, CT scans for emergency diagnostics
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <Card class="max-w-4xl mx-auto bg-gradient-to-r from-primary to-primary/90 rounded-lg shadow-xl md:p-12 text-white text-center">
              <CardContent className="p-8 text-center">
                <h2 className="text-3xl font-bold mb-4">Experience Our World-Class Facilities</h2>
                <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                  Schedule a visit to tour our hospital and see our state-of-the-art facilities firsthand. Our team will
                  be happy to show you around.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button size="lg" variant="secondary" className="shadow-lg font-heading" asChild>
                      <a
                        href="/book-now"
                        rel="noopener noreferrer"
                      >
                      <Calendar className="w-5 h-5 mr-2" />
                      Book an Appointment
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white text-white hover:bg-white hover:text-primary font-heading bg-transparent"
                    asChild
                  >
                    <Link to="/contact">
                      Contact Us
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
    </div>
    </>
  )
}
